import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly error: Error | null;
}

/**
 * Top-level error boundary. If any screen throws during render, we catch
 * it here instead of showing a blank white page. On recovery the user
 * can reload the app. Error messages are displayed verbatim — do NOT
 * include key material or PIN in any thrown Error; consumers of this
 * component must not `throw new Error(\`... ${secret} ...\`)`.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // No telemetry — spec §15.5 forbids analytics/crash reports. Dev-only log.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary caught:', error, info);
    }
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-text-primary">
          <h1 className="text-2xl font-semibold">Something went wrong</h1>
          <p className="max-w-md text-center text-text-secondary">
            {this.state.error.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-xl bg-white px-6 py-3 font-medium text-black"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
